import {
  createScreenShell, createMenuHeader, createCrestButton, createCopyButton, createInviteFriendButton,
  createSelectorControl, createSidePicker, el, withFocusPreserved,
} from '@bn-games/ui-shell';
import { ROOM_CODE_LENGTH } from '@bn-games/network-protocol';
import type { LobbyInfo } from '@bn-games/room-client';
import { t } from '../../i18n/I18n.js';
import { roomErrorText } from '../roomErrorText.js';
import { CLOCK_PRESETS, type ClockPreset } from '../../game/clock.js';
import type { Color } from '../../game/types.js';
import type { ChessMenuContext, MenuScreen } from '../types.js';

type ClockChoice = ClockPreset | 'none';
const CLOCK_CHOICES: readonly ClockChoice[] = ['none', ...CLOCK_PRESETS];

function clockLabel(choice: ClockChoice): string {
  return choice === 'none' ? t('online.create.noClock') : t(`settings.clockPresets.${choice}`);
}

function shareLinkFor(code: string): string {
  return `${window.location.origin}${window.location.pathname}#room=${code}`;
}

function formatRoomCode(code: string | null): string {
  return code === null ? '·'.repeat(ROOM_CODE_LENGTH) : code.split('').join(' ');
}

function statusText(info: LobbyInfo): { readonly text: string; readonly waiting: boolean } {
  switch (info.state) {
    case 'connecting':
      return { text: t('online.status.connecting'), waiting: true };
    case 'creating':
      return { text: t('online.status.creating'), waiting: true };
    case 'ready':
      return { text: t('online.status.opponentJoined'), waiting: false };
    case 'error':
      return { text: roomErrorText(info, t('online.status.error')), waiting: false };
    default:
      return { text: t('online.status.waitingForOpponent'), waiting: true };
  }
}

/**
 * Two phases on one screen: first the host picks a side and a time control
 * and opens the room; then the room code is shown until the opponent arrives
 * (the room host mounts the board itself once that happens).
 */
export function createOnlineCreateScreen(): MenuScreen {
  let root: HTMLElement | null = null;
  let frame: HTMLElement | null = null;
  let unsubRoom: (() => void) | null = null;
  let created = false;

  function backRow(ctx: ChessMenuContext): HTMLElement {
    return el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => {
          if (created) ctx.online.leaveRoom();
          ctx.navigate('play');
        },
      }),
    ]);
  }

  function formView(ctx: ChessMenuContext): HTMLElement {
    const form = ctx.online.getForm();
    const repaint = (): void => withFocusPreserved(() => paint(ctx), root ?? undefined);
    const side = createSidePicker<Color>({
      label: t('online.create.sideLabel'),
      leftValue: 'w',
      leftLabel: t('common.white'),
      rightValue: 'b',
      rightLabel: t('common.black'),
      value: form.color,
      onChange: (color) => {
        ctx.online.setForm({ color });
        repaint();
      },
    });
    const clock = createSelectorControl<ClockChoice>({
      label: t('online.create.clockLabel'),
      value: form.clock ?? 'none',
      options: CLOCK_CHOICES,
      format: clockLabel,
      onChange: (choice) => {
        ctx.online.setForm({ clock: choice === 'none' ? null : choice });
        repaint();
      },
    });
    const create = createCrestButton({
      label: t('online.create.create'),
      primary: true,
      onClick: () => {
        created = true;
        ctx.online.createRoom();
        paint(ctx);
      },
    });
    return el('div', { class: 'about-center' }, [
      side,
      el('div', { class: 'online-label', text: t('online.create.clockLabel') }),
      clock,
      el('div', { class: 'button-stack' }, [create]),
    ]);
  }

  function roomView(ctx: ChessMenuContext): HTMLElement {
    const info = ctx.online.getRoomInfo();
    const status = statusText(info);
    const center = el('div', { class: 'online-center' }, [
      el('div', { class: 'online-label', text: t('online.create.codeLabel') }),
      el('div', { class: 'online-code', text: formatRoomCode(info.roomCode) }),
    ]);
    const roomCode = info.roomCode;
    if (roomCode !== null) {
      center.appendChild(el('div', { class: 'button-row' }, [
        createCopyButton({ label: t('online.share.copyCode'), copiedLabel: t('online.share.copied'), getText: () => roomCode }),
        createCopyButton({ label: t('online.share.copyLink'), copiedLabel: t('online.share.copied'), getText: () => shareLinkFor(roomCode) }),
      ]));
      // Null inside an embedded shell, where a room-code invite makes no sense.
      const invite = createInviteFriendButton({ game: 'chess', roomCode });
      if (invite !== null) center.appendChild(el('div', { class: 'button-row' }, [invite]));
    }
    center.append(
      el('div', { class: 'online-hint', text: t('online.create.share') }),
      el('div', { class: 'online-hint', text: t('online.create.clockInfo', { clock: clockLabel(ctx.online.getForm().clock ?? 'none') }) }),
      el('div', { class: status.waiting ? 'online-status waiting-dots' : 'online-status', text: status.text }),
    );
    return center;
  }

  function paint(ctx: ChessMenuContext): void {
    if (root === null) return;
    frame?.remove();
    const shell = createScreenShell();
    frame = shell.frame;
    shell.body.append(
      createMenuHeader({ title: t('online.create.title') }),
      created ? roomView(ctx) : formView(ctx),
      backRow(ctx),
    );
    root.appendChild(frame);
  }

  return {
    mount(rootEl, ctx) {
      root = rootEl;
      created = false;
      unsubRoom = ctx.online.onRoomUpdate(() => {
        if (created) paint(ctx);
      });
      paint(ctx);
    },
    unmount() {
      unsubRoom?.();
      unsubRoom = null;
      frame?.remove();
      frame = null;
      root = null;
      created = false;
    },
  };
}
